import type { NavigationIntentMobileBehavior } from '../types';
import { managedEmployerOrgSlugStorageKey } from '@/constants';
import { getUiSurface, type UiSurface } from '@/lib/ui-surface';

export const NAVIGATION_DELAY_MIN_MS = 750;
export const NAVIGATION_DELAY_MAX_MS = 8000;
export const NAVIGATION_DELAY_DESKTOP_DEFAULT_MS = 3000;
export const NAVIGATION_DELAY_MOBILE_DEFAULT_MS = 4000;

export function normalizeNavigationMobileBehavior(value: unknown): NavigationIntentMobileBehavior {
  return value === 'switch' || value === 'stay' || value === 'prompt' ? value : 'prompt';
}

export function clampNavigationDelay(delayMs: unknown, isMobile: boolean): number {
  const fallback = isMobile ? NAVIGATION_DELAY_MOBILE_DEFAULT_MS : NAVIGATION_DELAY_DESKTOP_DEFAULT_MS;
  const raw = typeof delayMs === 'number' && Number.isFinite(delayMs) ? delayMs : fallback;
  return Math.min(NAVIGATION_DELAY_MAX_MS, Math.max(NAVIGATION_DELAY_MIN_MS, raw));
}

export function shouldAutoOpenNavigationIntent(
  isMobile: boolean,
  mobileBehavior: NavigationIntentMobileBehavior,
  autoOpen?: boolean
): boolean {
  if (autoOpen === false) {
    return false;
  }
  return !(isMobile && mobileBehavior === 'prompt');
}

/** Pathname + search for agent session context (tabs, query params). */
export function currentAppRoute(): string {
  if (typeof window === 'undefined') {
    return '/';
  }
  const { pathname, search } = window.location;
  return `${pathname}${search || ''}`;
}

/** Active employer organisation slug from switcher localStorage (TalentCo multi-org). */
export function currentActiveEmployerOrganisationSlug(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const slug = window.localStorage.getItem(managedEmployerOrgSlugStorageKey())?.trim();
    return slug || null;
  } catch {
    return null;
  }
}

/** Optional discourse listening scope set by AgentListeningProvider consumers. */
let discourseListeningSessionId: string | null = null;
let discourseListeningProfileId: string | null = null;

export function setDiscourseListeningAgentContext(
  sessionId: string | null,
  profileId: string | null = null,
): void {
  discourseListeningSessionId = sessionId;
  discourseListeningProfileId = profileId;
}

/** Agent scope payload — route, switcher slug, and current create/edit object when set. */
export function employerAgentSessionContext(): {
  current_route: string;
  active_organisation_slug?: string;
  ui_surface?: UiSurface;
  discourse_session_id?: string;
  live_lens_profile_id?: string;
} {
  const current_route = currentAppRoute();
  const active_organisation_slug = currentActiveEmployerOrganisationSlug();
  const ui_surface = getUiSurface() ?? undefined;
  return {
    current_route,
    ...(active_organisation_slug ? { active_organisation_slug } : {}),
    ...(ui_surface ? { ui_surface } : {}),
    ...(discourseListeningSessionId ? { discourse_session_id: discourseListeningSessionId } : {}),
    ...(discourseListeningProfileId ? { live_lens_profile_id: discourseListeningProfileId } : {}),
  };
}
